import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PurlyService } from '@jeppesen-foreflight/jmkw-core';
import { BasicButtonComponent, ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import {
  ButtonInstance,
  ButtonParams,
  JepGroupLayer,
  JepTileLayer,
  Plugin,
  PluginIDs,
  PluginService,
  ThemeColor
} from '@jeppesen-foreflight/jmkw-types';
import { Observable, Subscription, map, skip, take } from 'rxjs';
import { MetricsService } from '@jeppesen-foreflight/jmkw-analytics';
import { NGXLogger } from 'ngx-logger';
import { TerrainLegendComponent } from './terrain-legend.component';
import { getPurlyTerrainUrls } from '../aviation/utilities/purly-url-mapping.util';

const pluginId: PluginIDs = 'terrain';

export const TERRAIN_TOGGLE_BUTTON: ButtonParams = {
  id: pluginId,
  iconName: 'icon-terrain',
  buttonComponent: BasicButtonComponent,
  automationKey: 'terrain-toggle-button',
  tooltipText: 'Terrain',
  stateFn: (button, layer) => (button.active = layer.visible),
  submit: (button: ButtonInstance) => {
    const layer = button.toolkitApiService.getLayerById<JepTileLayer>(
      button.id
    );
    layer?.jepTools.toggleVisibility();
    }
};

@Injectable({
  providedIn: 'root'
})
export class TerrainPluginService implements PluginService {
  private readonly _api = inject(ToolkitApiService);
  private readonly _purlyService = inject(PurlyService);
  private readonly _destroyRef = inject(DestroyRef);
  protected readonly metrics = inject(MetricsService);
  protected readonly logger = inject(NGXLogger);
  readonly id: PluginIDs = pluginId;
  private readonly _subscriptions = new Subscription();
  private readonly _groupLayer = new JepGroupLayer({
    blendMode: 'destination-over'
  });
  private _terrainPlugin?: Plugin;
  private _bindingDispose?: () => void;
  currentTheme?: ThemeColor;

  create(): Observable<Plugin> {
    return this._purlyService.initializationResult$.pipe(
      take(1),
      map(() => {
        const tileServiceUrls = this._purlyService.getTileServiceUrls();
        const terrainUrls = getPurlyTerrainUrls(tileServiceUrls);

        if (!terrainUrls.Light || !terrainUrls.Dark) {
          this.logger.warn(
            'Terrain plugin: No URLs returned from Purly service',
            { terrainUrls }
          );
        }

        this.initializeLayers(terrainUrls);
        this.setupBasemapSubscription();
        this.setupPurlyBinding();

        this._terrainPlugin = new Plugin({
          id: this.id,
          button: TERRAIN_TOGGLE_BUTTON,
          layer: this._groupLayer,
          legend: {
            legendComponent: TerrainLegendComponent,
            title: 'Terrain'
          }
        });

        return this._terrainPlugin;
      })
    );
  }

  private initializeLayers(terrainUrls: { Light: string; Dark: string }): void {
    const lightTerrainLayer = this.createTerrainLayer('Light', terrainUrls.Light);
    const darkTerrainLayer = this.createTerrainLayer('Dark', terrainUrls.Dark);

    this._groupLayer.add(lightTerrainLayer, 0);
    this._groupLayer.add(darkTerrainLayer, 1);
    this._groupLayer.visible = false;
  }

  private createTerrainLayer(type: 'Light' | 'Dark', url: string): JepTileLayer {
    return new JepTileLayer({
      id: `terrain-${type.toLowerCase()}`,
      zOrder: 0,
      label: 'Terrain',
      url,
      visible: false,
      resampling: true,
      minScale: 0,
      maxScale: 0
    });    
  }

  private setupBasemapSubscription(): void {
    const sub = this._api
      .currentBasemap$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((basemap) => this.changeTheme(basemap.theme));

    this._subscriptions.add(sub);
  }

  private changeTheme(theme: ThemeColor): void {
    const [lightMap, darkMap] = [this.getLightMap(), this.getDarkMap()];

    lightMap.visible = theme !== 'dark';
    darkMap.visible = theme === 'dark';

    this.currentTheme = theme;
  }

  private setupPurlyBinding(): void {
    // Clean up existing subscription if setupPurlyBinding is called multiple times
    this._bindingDispose?.();

    // Subscribe to Purly URL updates for when URLs need to be refreshed
    // Skip(1) to ignore the initial load, only react to subsequent refreshes
    // takeUntilDestroyed ensures automatic cleanup when component is destroyed
    this._purlyService.initializationResult$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const tileServiceUrls = this._purlyService.getTileServiceUrls();
        const terrainUrls = getPurlyTerrainUrls(tileServiceUrls);

        const lightMap = this.getLightMap();
        const darkMap = this.getDarkMap();

        if (terrainUrls.Light && terrainUrls.Light !== lightMap.url) {
          this.logger.debug('Updating terrain light layer URL from Purly service', {
            newUrl: terrainUrls.Light
          });

          const updatedLightLayer = this.createTerrainLayer('Light', terrainUrls.Light);
          updatedLightLayer.visible = lightMap.visible;
          this._groupLayer.remove(lightMap);
          this._groupLayer.add(updatedLightLayer, 0);
        }

        if (terrainUrls.Dark && terrainUrls.Dark !== darkMap.url) {
          this.logger.debug('Updating terrain dark layer URL from Purly service', {
            newUrl: terrainUrls.Dark
          });

          const updatedDarkLayer = this.createTerrainLayer('Dark', terrainUrls.Dark);
          updatedDarkLayer.visible = darkMap.visible;
          this._groupLayer.remove(darkMap);
          this._groupLayer.add(updatedDarkLayer, 1);
        }
      });
  }

  private getLightMap(): JepTileLayer {
    return this._groupLayer.layers.getItemAt(0) as JepTileLayer;
  }

  private getDarkMap(): JepTileLayer {
    return this._groupLayer.layers.getItemAt(1) as JepTileLayer;
  }
}